<template>
  <div ref="container" class="h-full w-full">
    <svg :width="width" :height="width">
      <circle
        class="stroke-secondary-container"
        :cx="width / 2"
        :cy="width / 2"
        :r="paddedDiameter / 2"
        :stroke-width="thickness"
        :stroke-dasharray="circumference"
        stroke-linecap="round"
        :stroke-dashoffset="animatedStrokeDashoffsetTrack"
        :transform="rotateTrack"
        :transform-origin="`${width / 2} ${width / 2}`"
        fill="transparent"
      />
      <path
        class="stroke-primary fill-none"
        :class="[wavePath == '' && 'transition-none', wavePath != '' && '']"
        :stroke-width="thickness"
        :d="wavePath"
        stroke-linecap="round"
        pathLength="1"
        stroke-dasharray="1"
        :stroke-dashoffset="animatedStrokeDashoffset"
      ></path>
    </svg>
  </div>
</template>
<script lang="ts" setup>
  import { useElementBounding, useTransition } from '@vueuse/core'
  import { computed, onMounted, ref, useTemplateRef, watch } from 'vue'

  interface Props {
    wavelength?: number
    thickness?: number
    radius?: number
    percentage?: number
    amplitude?: number
  }

  const { wavelength = 35, thickness = 8, percentage = 90, amplitude = 2 } = defineProps<Props>()

  const container = useTemplateRef('container')
  const { width } = useElementBounding(container)

  const paddedDiameter = computed(() => width.value - thickness - 2 * amplitude)
  const circumference = computed(() => 2 * Math.PI * (paddedDiameter.value / 2))

  const gapLength = computed(() => (percentage == 0 ? 0 : thickness + 4))
  const rotateTrack = computed(() => {
    return 'rotate(' + -(90 + (gapLength.value / circumference.value) * 360) + ')'
  })
  const wavePath = computed(() => {
    let p = ''
    const b = Math.floor(circumference.value / wavelength)
    for (let degree = 0; degree < 360; degree += 0.5) {
      const rad = degree * (Math.PI / 180)

      const factor = Math.sin(rad * b)
      const { x, y } = getCirclePoint(degree, factor)

      if (degree == 0) {
        p += 'M '
      } else {
        p += ' L '
      }

      p += `${x} ${y}`
    }

    return p
  })

  const getCirclePoint = (degree: number, factor: number) => {
    // Define the properties of the circle based on the requirements
    const radius = paddedDiameter.value / 2 - amplitude * factor // Diameter is 1, so radius is 0.5
    const centerX = width.value / 2
    const centerY = width.value / 2

    // 1. Adjust the angle
    // The user's 0-degree is at the top, but in standard trigonometry,
    // 0 degrees is on the right side. We need to offset the angle by -90 degrees.
    const angleInDegrees = degree - 90

    // 2. Convert degrees to radians
    // JavaScript's Math.cos() and Math.sin() expect angles in radians.
    const angleInRadians = angleInDegrees * (Math.PI / 180)

    // 3. Calculate the coordinates
    // Use the standard trigonometric formulas and then adjust for our radius and center.
    // x = centerX + radius * cos(angle)
    // y = centerY + radius * sin(angle)
    const x = centerX + radius * Math.cos(angleInRadians)
    const y = centerY + radius * Math.sin(angleInRadians)

    return { x, y }
  }

  const strokeDashoffset = ref(1)
  const strokeDashoffsetTrack = ref(-2 * gapLength.value)
  onMounted(() => {
    setTimeout(() => {
      strokeDashoffset.value = 1 - percentage / 100
      strokeDashoffsetTrack.value = (percentage / 100) * -circumference.value - 2 * gapLength.value
    }, 10)
  })

  watch(
    () => percentage,
    (newPercentage: number) => {
      strokeDashoffset.value = 1 - newPercentage / 100
      strokeDashoffsetTrack.value =
        (newPercentage / 100) * -circumference.value - 2 * gapLength.value
    },
  )

  const animatedStrokeDashoffset = useTransition(strokeDashoffset, {
    duration: 4000,
    transition: [0.31, 0.94, 0.34, 1],
  })
  const animatedValue = useTransition(strokeDashoffsetTrack, {
    duration: 4000,
    transition: [0.31, 0.94, 0.34, 1],
  })
  const animatedStrokeDashoffsetTrack = computed(() => {
    return Math.max(animatedValue.value, -circumference.value)
  })
</script>
